<template>
  <div class="flex flex-wrap mb-2">
    <div ref="adBudgetAutoAdjust" class="w-full lg:w-3/4 flex-1 min-w-0 bg-white rounded-xl space-y-6 p-3 lg:p-6" style="position:relative">
      <!-- 標題區塊 -->
      <div>
        <span class="text-grap-700 align-middle">{{ lang['components']['Event']['AdBudgetAutoAdjust']['title'] }}</span>
        <i
          class="pi pi-exclamation-circle align-middle text-gray-300 ml-2"
          v-tooltip.top="lang['components']['Event']['AdBudgetAutoAdjust']['title_tooltip']"
        ></i>
      </div>

      <!-- 功能開關 -->
      <div class="flex-1 space-y-4">
        <div>
          <div class="flex items-center space-x-4">
            <div class="mt-2">
              <InputSwitch :disabled="editPageType" v-model="viewDatalistData.raise_enabled"/>
            </div>
            <div class="text-sm leading-6 text-gray-400">{{ lang['components']['Event']['AdBudgetAutoAdjust']['enable_text'] }}</div>
          </div>
        </div>

        <!-- 以下內容在 raise_enabled = true 時顯示 -->
        <div v-if="isAutoAdjustEnabled">
          
          <!-- ① 判斷層級 -->
          <div class="border rounded-xl p-4 mb-4">
            <h3 class="text-sm font-medium text-gray-700 mb-4">{{ lang['components']['Event']['AdBudgetAutoAdjust']['evaluation_level'] }}</h3>
            
            <div class="flex flex-col lg:flex-row lg:items-center gap-2 lg:space-x-6">
              <div class="flex items-center">
                <RadioButton
                  v-model="viewDatalistData.scope_level"
                  inputId="scope_campaign"
                  value="CAMPAIGN"
                  :disabled="editPageType"
                />
                <label for="scope_campaign" class="ml-2 text-sm text-gray-700">{{ lang['components']['Event']['AdBudgetAutoAdjust']['campaign_level'] }}</label>
              </div>
              <div class="flex items-center">
                <RadioButton
                  v-model="viewDatalistData.scope_level"
                  inputId="scope_adset"
                  value="ADSET"
                  :disabled="editPageType"
                />
                <label for="scope_adset" class="ml-2 text-sm text-gray-700">{{ lang['components']['Event']['AdBudgetAutoAdjust']['adset_level'] }}</label>
              </div>
            </div>
          </div>

          <!-- ② 基本設定 -->
          <div class="border rounded-xl p-4 mb-4">
            <h3 class="text-sm font-medium text-gray-700 mb-4">{{ lang['components']['Event']['AdBudgetAutoAdjust']['basic_settings'] }}</h3>
            
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">

              <!-- 加減碼幅度 -->
              <div class="min-w-0">
                <label class="block text-sm text-gray-700 mb-1">
                  {{ lang['components']['Event']['AdBudgetAutoAdjust']['step_pct_label'] }}
                  <i
                    class="pi pi-exclamation-circle align-middle text-gray-300 ml-1"
                    v-tooltip.top="lang['components']['Event']['AdBudgetAutoAdjust']['step_pct_tooltip']"
                  ></i>
                </label>
                <div class="flex items-center">
                  <Dropdown
                    v-model="viewDatalistData.step_pct"
                    :options="stepPctOptions"
                    :disabled="editPageType"
                    class="w-full rounded-md"
                    :placeholder="lang['components']['Event']['AdBudgetAutoAdjust']['placeholder_select']"
                    appendTo="body"

                  />
                  <span class="ml-2 text-sm text-gray-500">%</span>
                </div>
                <div class="text-xs text-gray-400 mt-1">
                  ({{ lang['components']['Event']['AdBudgetAutoAdjust']['condition_met'] }} +{{ viewDatalistData.step_pct || 10 }}%, {{ lang['components']['Event']['AdBudgetAutoAdjust']['condition_not_met'] }} -{{ viewDatalistData.step_pct || 10 }}%)
                </div>
              </div>

              <!-- 預算下限 -->
              <div class="min-w-0">
                <label class="block text-sm text-gray-700 mb-1">
                  {{ lang['components']['Event']['AdBudgetAutoAdjust']['min_budget'] }}
                  <i
                    class="pi pi-exclamation-circle align-middle text-gray-300 ml-1"
                    v-tooltip.top="lang['components']['Event']['AdBudgetAutoAdjust']['min_budget_tooltip']"
                  ></i>
                </label>
                <div class="flex items-center">
                  <InputNumber
                    v-model="viewDatalistData.min_daily_budget"
                    :disabled="editPageType"
                    :min="minBudgetLimit"
                    :max="maxBudgetLimitForMin"
                    class="w-full"
                    :class="{ 'p-invalid': minBudgetError }"
                    :placeholder="lang['components']['Event']['AdBudgetAutoAdjust']['placeholder_enter']"
                    @blur="validateMinBudget"
                  />
                  <span class="ml-2 text-sm text-gray-500">{{ currencySymbolData }}</span>
                </div>
                <div v-if="minBudgetError" class="text-xs text-red-500 mt-1">
                  {{ lang['components']['Event']['AdBudgetAutoAdjust']['min_budget_error_prefix'] }} {{ minBudgetLimit }} {{ lang['components']['Event']['AdBudgetAutoAdjust']['error_to'] }} {{ maxBudgetLimitForMin }} {{ lang['components']['Event']['AdBudgetAutoAdjust']['error_between_suffix'] }}
                </div>
                <div v-else class="text-xs text-gray-400 mt-1">
                  {{ lang['components']['Event']['AdBudgetAutoAdjust']['allowed_range'] }}：{{ minBudgetLimit }} ~ {{ maxBudgetLimitForMin }} {{ currencySymbolData }}
                </div>
              </div>

              <!-- 預算上限 -->
              <div class="min-w-0">
                <label class="block text-sm text-gray-700 mb-1">
                  {{ lang['components']['Event']['AdBudgetAutoAdjust']['max_budget'] }}
                  <i
                    class="pi pi-exclamation-circle align-middle text-gray-300 ml-1"
                    v-tooltip.top="lang['components']['Event']['AdBudgetAutoAdjust']['max_budget_tooltip']"
                  ></i>
                </label>
                <div class="flex items-center">
                  <InputNumber 
                    v-model="viewDatalistData.max_daily_budget" 
                    :disabled="editPageType"
                    :min="minMaxBudgetLimit"
                    class="w-full"
                    :class="{ 'p-invalid': maxBudgetError }"
                    :placeholder="lang['components']['Event']['AdBudgetAutoAdjust']['placeholder_enter']"
                    @blur="validateMaxBudget"
                  />
                  <span class="ml-2 text-sm text-gray-500">{{ currencySymbolData }}</span>
                </div>
                <div v-if="maxBudgetError" class="text-xs text-red-500 mt-1">
                  {{ lang['components']['Event']['AdBudgetAutoAdjust']['max_budget_error_prefix'] }} {{ minMaxBudgetLimit }}（{{ lang['components']['Event']['AdBudgetAutoAdjust']['original_budget_text'] }} {{ originalDailyBudget }} + {{ viewDatalistData.step_pct || 10 }}%）
                </div>
              </div>
            </div>
          </div>

          <!-- ③ 頻率 -->
          <div class="border rounded-xl p-4 mb-4">
            <h3 class="text-sm font-medium text-gray-700 mb-4">{{ lang['components']['Event']['AdBudgetAutoAdjust']['frequency'] }}</h3>
            
            <div>
              <div>
                <label class="block text-sm text-gray-700 mb-1">
                  {{ lang['components']['Event']['AdBudgetAutoAdjust']['eval_frequency'] }}
                  <i
                    class="pi pi-exclamation-circle align-middle text-gray-300 ml-1"
                    v-tooltip.top="lang['components']['Event']['AdBudgetAutoAdjust']['eval_frequency_tooltip']"
                  ></i>
                </label>
                <Dropdown
                  v-model="viewDatalistData.adjust_times_per_day"
                  :options="frequencyOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="w-full rounded-md"
                  :placeholder="lang['components']['Event']['AdBudgetAutoAdjust']['placeholder_select']"
                  :disabled="editPageType"
                  appendTo="body"
                />
                <div class="text-xs text-gray-400 mt-1">
                  1 = 24HR / 2 = 12HR / 3 = 8HR / 4 = 6HR / 6 = 4HR
                </div>
              </div>
            </div>
          </div>

          <!-- ④ 觸發條件（指標 + 時間軸） -->
          <div class="border rounded-xl p-4 mb-4">
            <h3 class="text-sm font-medium text-gray-700 mb-4">{{ lang['components']['Event']['AdBudgetAutoAdjust']['trigger_conditions'] }}</h3>
            
            <!-- 時間軸 -->
            <div class="mb-4">
              <label class="block text-sm text-gray-700 mb-1">
                {{ lang['components']['Event']['AdBudgetAutoAdjust']['time_window_prefix'] }}
                <Dropdown
                  v-model="viewDatalistData.lookback_window_days"
                  :options="lookbackOptions"
                  class="mx-2 w-20"
                  :disabled="editPageType"
                  appendTo="body"
                />
                {{ lang['components']['Event']['AdBudgetAutoAdjust']['time_window_suffix'] }}
              </label>
              <div class="text-xs text-gray-400 mt-1">
                {{ lang['components']['Event']['AdBudgetAutoAdjust']['perf_calc_prefix'] }} {{ viewDatalistData.lookback_window_days || 1 }} {{ lang['components']['Event']['AdBudgetAutoAdjust']['perf_calc_suffix'] }}
              </div>
            </div>

            <!-- 條件式 -->
            <div>
              <label class="block text-sm text-gray-700 mb-2">{{ lang['components']['Event']['AdBudgetAutoAdjust']['conditions_label'] }}：</label>
              <div class="overflow-x-auto">
              <table class="border-collapse border border-gray-300 text-sm" style="min-width: 340px; width: 100%;">
                <thead>
                  <tr class="bg-gray-50">
                    <th class="border border-gray-300 px-2 lg:px-4 py-2 text-sm text-gray-700 text-center" style="width: 100px;">{{ lang['components']['Event']['AdBudgetAutoAdjust']['th_metric'] }}</th>
                    <th class="border border-gray-300 px-1 lg:px-4 py-2 text-sm text-gray-700 text-center whitespace-nowrap" style="width: 45px;">{{ lang['components']['Event']['AdBudgetAutoAdjust']['th_comparison'] }}</th>
                    <th class="border border-gray-300 px-2 lg:px-4 py-2 text-sm text-gray-700 text-center">{{ lang['components']['Event']['AdBudgetAutoAdjust']['th_threshold'] }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="metric in metricsConfig" :key="metric.field">
                    <td class="border border-gray-300 px-2 lg:px-4 py-2 text-center whitespace-nowrap">
                      <div class="flex items-center justify-center">
                        <RadioButton
                          v-model="viewDatalistData.raise_filter.field"
                          :inputId="metric.field"
                          :value="metric.field"
                          :disabled="editPageType"
                          @change="onMetricChange(metric.field)"
                        />
                        <label :for="metric.field" class="ml-2 text-sm">{{ metric.field }}</label>
                      </div>
                    </td>
                    <td class="border border-gray-300 px-1 py-2 text-center text-sm text-gray-600 whitespace-nowrap">
                      {{ metric.symbol }}
                    </td>
                    <td class="border border-gray-300 px-2 lg:px-4 py-2 text-center">
                      <InputNumber
                        v-model="metricValues[metric.field]"
                        :disabled="editPageType || viewDatalistData.raise_filter.field !== metric.field"
                        :minFractionDigits="metric.field === 'ROAS' ? 1 : 0"
                        :maxFractionDigits="metric.field === 'ROAS' ? 2 : 0"
                        class="w-full"
                        :class="{ 'opacity-50': viewDatalistData.raise_filter.field !== metric.field }"
                        :placeholder="lang['components']['Event']['AdBudgetAutoAdjust']['placeholder_enter']"
                        @update:modelValue="(val) => onMetricValueChange(metric.field, val)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              </div>
              <div class="text-xs text-gray-400 mt-2">
                {{ lang['components']['Event']['AdBudgetAutoAdjust']['metrics_available'] }}<br/>
                📌 {{ lang['components']['Event']['AdBudgetAutoAdjust']['metrics_note'] }}
              </div>
            </div>
          </div>

          <!-- 規則摘要 -->
          <div v-if="showRuleSummary" class="border rounded-xl p-4 bg-gray-50">
            <h3 class="text-sm font-medium text-gray-700 mb-3">📋 {{ lang['components']['Event']['AdBudgetAutoAdjust']['rule_summary'] }}</h3>
            <ul class="text-sm text-gray-600 space-y-1">
              <li>• {{ lang['components']['Event']['AdBudgetAutoAdjust']['summary_level'] }}：{{ viewDatalistData.scope_level === 'CAMPAIGN' ? lang['components']['Event']['AdBudgetAutoAdjust']['summary_campaign'] : lang['components']['Event']['AdBudgetAutoAdjust']['summary_adset'] }}</li>
              <li>• {{ lang['components']['Event']['AdBudgetAutoAdjust']['summary_metric'] }}：{{ viewDatalistData.raise_filter.field }} {{ currentMetricSymbol }} {{ viewDatalistData.raise_filter.value || '-' }}</li>
              <li>• {{ lang['components']['Event']['AdBudgetAutoAdjust']['summary_time_window'] }} {{ viewDatalistData.lookback_window_days || 1 }} {{ lang['components']['Event']['AdBudgetAutoAdjust']['summary_day'] }}</li>
              <li>• {{ lang['components']['Event']['AdBudgetAutoAdjust']['summary_frequency'] }}：{{ lang['components']['Event']['AdBudgetAutoAdjust']['summary_daily'] }} {{ viewDatalistData.adjust_times_per_day || 1 }} {{ lang['components']['Event']['AdBudgetAutoAdjust']['summary_times'] }}</li>
              <li>• {{ lang['components']['Event']['AdBudgetAutoAdjust']['summary_good'] }} +{{ viewDatalistData.step_pct || 10 }}%</li>
              <li>• {{ lang['components']['Event']['AdBudgetAutoAdjust']['summary_bad'] }} −{{ viewDatalistData.step_pct || 10 }}%</li>
              <li>• {{ lang['components']['Event']['AdBudgetAutoAdjust']['summary_range'] }}：{{ viewDatalistData.min_daily_budget || '-' }} ~ {{ viewDatalistData.max_daily_budget || '-' }} {{ currencySymbolData }}</li>
            </ul>
          </div>

        </div>
      </div>
    </div>
    <div class="hidden lg:block w-1/4 rounded-xl space-y-2 px-4 event-tips-sidebar"></div>
  </div>
</template>

<script>
  import InputSwitch from 'primevue/inputswitch';
  import Dropdown from "primevue/dropdown";
  import InputNumber from 'primevue/inputnumber';
  import RadioButton from 'primevue/radiobutton';

  export default {
    name: 'AdBudgetAutoAdjust',
    props: {
      editPage: {
        type: Boolean,
        default: false,
      },
      viewDatalist: {
        type: Object,
        default: () => ({}),
      },
      lang: {
        type: Object,
        default: () => ({}),
      },
      currencySymbol: {
        type: String,
        default: 'TWD',
      },
      originalDailyBudget: {
        type: Number,
        default: 0,
      },
    },
    components: {
      InputSwitch,
      Dropdown,
      InputNumber,
      RadioButton,
    },
    data() {
        const metricValues = {
            CPA: null,
            ROAS: null,
            CPC: null,
        };
        if (
            this.viewDatalist &&
            this.viewDatalist.raise_filter &&
            this.viewDatalist.raise_filter.field &&
            this.viewDatalist.raise_filter.value !== undefined
        ) {
            metricValues[this.viewDatalist.raise_filter.field] = this.viewDatalist.raise_filter.value;
        }
        return {
            minBudgetError: false,
            maxBudgetError: false,
            frequencyOptions: [
            { label: '每日 1 次', value: 1 },
            { label: '每日 2 次', value: 2 },
            { label: '每日 3 次', value: 3 },
            { label: '每日 4 次', value: 4 },
            { label: '每日 6 次', value: 6 },
            ],
            stepPctOptions: [5, 10, 15, 20, 25, 30],
            lookbackOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
            metricsConfig: [
            { field: 'CPA', operator: 'LESS_THAN_EQUAL', symbol: '<=' },
            { field: 'ROAS', operator: 'GREATER_THAN_EQUAL', symbol: '>=' },
            { field: 'CPC', operator: 'LESS_THAN_EQUAL', symbol: '<=' },
            ],
            metricValues,
        };
    },
    watch: {
      lang: {
        handler(newLang) {
          const aba = newLang?.components?.Event?.AdBudgetAutoAdjust;
          if (aba) {
            this.frequencyOptions[0].label = aba.freq_1;
            this.frequencyOptions[1].label = aba.freq_2;
            this.frequencyOptions[2].label = aba.freq_3;
            this.frequencyOptions[3].label = aba.freq_4;
            this.frequencyOptions[4].label = aba.freq_6;
          }
        },
        immediate: true
      },
        'viewDatalist.raise_filter': {
            handler(newVal) {
            if (
                newVal &&
                newVal.field &&
                newVal.value !== undefined &&
                newVal.value !== null
            ) {
                this.metricValues[newVal.field] = newVal.value;
            } else {
                console.log('Watch condition not met, newVal:', newVal);
            }
            },
            immediate: true,
            deep: true
        }
    },
    mounted() {
      this._dropdownScrollFix = (e) => {
        if (this.$el && this.$el.contains(e.target) && e.target.closest('.p-dropdown')) {
          const orig = Element.prototype.scrollIntoView;
          Element.prototype.scrollIntoView = function() {};
          setTimeout(() => { Element.prototype.scrollIntoView = orig; }, 300);
        }
      };
      document.addEventListener('mousedown', this._dropdownScrollFix, true);
    },
    beforeDestroy() {
      if (this._dropdownScrollFix) {
        document.removeEventListener('mousedown', this._dropdownScrollFix, true);
      }
    },
    methods: {
      onMetricChange(field) {
        // 切換指標時，更新 operator 和 value
        const metric = this.metricsConfig.find(m => m.field === field);
        if (metric) {
          this.viewDatalistData.raise_filter.operator = metric.operator;
          this.viewDatalistData.raise_filter.value = this.metricValues[field];
        }
      },
      onMetricValueChange(field, value) {
        // 當門檻值改變時，如果是當前選中的指標，同步更新到 raise_filter.value
        if (this.viewDatalistData.raise_filter.field === field) {
            // 確保 value 是數字或 null
            this.viewDatalistData.raise_filter.value = (typeof value === 'number' || value === null) ? value : null;
        }
        this.metricValues[field] = value;
      },
      // 驗證{{ lang['components']['Event']['AdBudgetAutoAdjust']['min_budget'] }}
      validateMinBudget() {
        const minLimit = this.minBudgetLimit;
        const maxLimit = this.maxBudgetLimitForMin;

        if (this.viewDatalistData.min_daily_budget < minLimit ||
            this.viewDatalistData.min_daily_budget > maxLimit) {
          this.minBudgetError = true;
          // 自動修正為下限值
          this.viewDatalistData.min_daily_budget = minLimit;
          setTimeout(() => {
            this.minBudgetError = false;
          }, 3000);
        } else {
          this.minBudgetError = false;
        }
      },
      // 驗證{{ lang['components']['Event']['AdBudgetAutoAdjust']['max_budget'] }}
      validateMaxBudget() {
        if (this.minMaxBudgetLimit && this.viewDatalistData.max_daily_budget < this.minMaxBudgetLimit) {
          this.maxBudgetError = true;
          // 自動修正為最小上限值
          this.viewDatalistData.max_daily_budget = this.minMaxBudgetLimit;
          setTimeout(() => {
            this.maxBudgetError = false;
          }, 3000);
        } else {
          this.maxBudgetError = false;
        }
      },
      // 驗證自動加碼/減碼設定
      checkData() {
        // 如果沒有啟用，則不需要驗證
        if (!this.viewDatalistData.raise_enabled) {
          return { isValid: true, errors: [] };
        }

        const L = this.lang?.['components']?.['Event']?.['AdBudgetAutoAdjust'] || {};
        const errors = [];

        // 驗證預算下限
        if (!this.viewDatalistData.min_daily_budget || this.viewDatalistData.min_daily_budget === null) {
          errors.push(L.err_min_budget);
        } else {
          const minLimit = this.minBudgetLimit;
          const maxLimit = this.maxBudgetLimitForMin;
          if (this.viewDatalistData.min_daily_budget < minLimit || this.viewDatalistData.min_daily_budget > maxLimit) {
            errors.push(`${L.min_budget_error_prefix} ${minLimit} ${L.error_to} ${maxLimit} ${L.error_between_suffix}`);
          }
        }

        // 驗證預算上限
        if (!this.viewDatalistData.max_daily_budget || this.viewDatalistData.max_daily_budget === null) {
          errors.push(L.err_max_budget);
        } else if (this.viewDatalistData.max_daily_budget < this.minMaxBudgetLimit) {
          errors.push(`${L.max_budget_error_prefix} ${this.minMaxBudgetLimit}`);
        }

        // 驗證加減碼幅度
        if (!this.viewDatalistData.step_pct || this.viewDatalistData.step_pct === null) {
          errors.push(L.err_step_pct);
        }

        // 驗證判斷頻率
        if (!this.viewDatalistData.adjust_times_per_day || this.viewDatalistData.adjust_times_per_day === null) {
          errors.push(L.err_frequency);
        }

        // 驗證觸發條件的門檻值
        if (!this.viewDatalistData.raise_filter.value || this.viewDatalistData.raise_filter.value === null || this.viewDatalistData.raise_filter.value === '') {
          errors.push(L.err_threshold);
        }

        return {
          isValid: errors.length === 0,
          errors: errors
        };
      },
    },
    computed: {
      editPageType: {
        get: function () {
          return this.editPage;
        },
        set: function (value) {
          this.$emit('update:editPageType', value);
        },
      },
      viewDatalistData: {
        get: function () {
          return this.viewDatalist;
        },
        set: function (value) {
          this.$emit('update:viewDatalist', value);
        },
      },
      currencySymbolData: {
        get: function () {
          return this.currencySymbol;
        },
        set: function (value) {
          this.$emit('update:currencySymbol', value);
        },
      },
      // 當前選中指標的符號
      currentMetricSymbol() {
        const field = this.viewDatalistData.raise_filter?.field;
        const metric = this.metricsConfig.find(m => m.field === field);
        return metric ? metric.symbol : '<=';
      },
      // 是否顯示規則摘要
      showRuleSummary() {
        return this.viewDatalistData.raise_enabled && 
            this.viewDatalistData.raise_filter?.field &&
            this.viewDatalistData.raise_filter?.value !== undefined &&
            this.viewDatalistData.raise_filter?.value !== null &&
            this.viewDatalistData.raise_filter?.value !== '';
      },
      isAutoAdjustEnabled() {
        return this.viewDatalistData.raise_enabled === true || this.viewDatalistData.raise_enabled === '1';
      },
      // 預算下限的最小值（總額的 80%，即 -20%）
      minBudgetLimit() {
        if (!this.originalDailyBudget) return 0;
        return Math.round(this.originalDailyBudget * 0.8);
      },
      // 預算下限的最大值（總額的 99%）
      maxBudgetLimitForMin() {
        if (!this.originalDailyBudget) return 0;
        return Math.round(this.originalDailyBudget * 0.99);
      },
      // 預算上限的最小值（原始預算 + 加減碼幅度）
      minMaxBudgetLimit() {
        if (!this.originalDailyBudget) return 0;
        const stepPct = this.viewDatalistData.step_pct || 10;
        return Math.round(this.originalDailyBudget * (1 + stepPct / 100));
      },
    },
  }
</script>

<style lang="scss" scoped>
  ::v-deep(.p-inputnumber) {
    .p-inputtext {
      padding: 0.5rem;
      border-radius: 0.375rem;
    }
  }

  ::v-deep(.p-inputnumber.p-invalid) {
    .p-inputtext {
      border-color: #ef4444;
    }
  }
  
  ::v-deep(.p-dropdown) {
    .p-dropdown-label {
      padding: 0.5rem;
    }
  }

  .grid > div {
    min-width: 0;
    overflow: hidden;
  }

  ::v-deep(.grid .p-inputnumber),
  ::v-deep(.grid .p-dropdown) {
    min-width: 0;
    max-width: 100%;
  }

  ::v-deep(.grid .p-inputtext),
  ::v-deep(.grid .p-dropdown-label) {
    min-width: 0;
    width: 100%;
  }

  table {
    border-collapse: collapse;
  }

  th, td {
    border: 1px solid #e5e7eb;
  }
</style>
